import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { StoreService } from '../core/store.service';
import { SelectBoxComponent } from '../shared/select-box.component';
import { DxTextBoxModule } from 'devextreme-angular';

@Component({
    selector: 'app-filters',
    imports: [FormsModule, SelectBoxComponent, DxTextBoxModule],
    template: `
        <div class="home-page-filters-wrapper ajax-filters-pro" x-style="margin-top: -73.9699px">
            <div class="home-page-search-area">
                <div class="home-page-search-row">
                    <dx-text-box
                        placeholder="Search for products..."
                        [showClearButton]="true"
                        [(value)]="searchValue"
                        (onEnterKey)="searchByPartNumber()"
                    ></dx-text-box>
                    <button type="button" class="search-button" (click)="searchByPartNumber()" aria-label="Search for products">
                        <i class="fas fa-solid fa-search"></i>
                    </button>
                </div>
            </div>
            <div class="home-page-filters">
                <div class="home-page-filter-selectors">
                    <div class="home-page-filter-selector">
                        <app-select-box
                            [items]="makes()"
                            [valueExpr]="'name'"
                            [placeholder]="'Make'"
                            (onValueChanged)="onMakeChanged($event)"
                        ></app-select-box>
                    </div>
                    <div class="home-page-filter-selector">
                        <app-select-box
                            [items]="models()"
                            [valueExpr]="'name'"
                            [placeholder]="'Model'"
                            (onValueChanged)="onModelChanged($event)"
                        ></app-select-box>
                    </div>
                    <div class="home-page-filter-selector">
                        <app-select-box
                            [items]="partTypes()"
                            [placeholder]="'Part Type'"
                            (onValueChanged)="onPartTypeChanged($event)"
                        ></app-select-box>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: `
        .home-page-filters-wrapper {
            background-color: rgba(0, 0, 0, 0.5);
            padding: 0;
            margin: 0;
        }

        .home-page-filters {
            text-align: center;
            padding: 10px 0;
        }

        .home-page-search-area {
            background: #fff;
            padding: 0 0 30px;
        }

        .home-page-search-row {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin: 0 auto;
            width: min(670px, calc(100% - 40px));

            dx-text-box {
                flex: 1;
            }

            .search-button {
                border: 0;
                background: transparent;
                color: #000;
                font-size: 30px;
                line-height: 1;
                cursor: pointer;
                padding: 4px 0;
            }
        }

        .home-page-filter-selectors {
            float: none;
            display: inline-flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: center;
            max-width: 100%;
        }

        .home-page-filter-selector {
            float: none;
            width: 200px;
            margin: 10px;

            app-select-box {
                display: block;
            }
        }

        @media (max-width: 768px) {
            .home-page-filter-selectors {
                display: flex;
                flex-direction: column;
                width: calc(100% - 80px);
                max-width: 340px;
                margin: 0 auto;
                gap: 8px;
            }

            .home-page-filter-selector {
                width: 100%;
                margin: 0;
            }
        }
    `
})
export class FiltersComponent {
    router = inject(Router);
    storeService = inject(StoreService);

    selectedMake = signal<string>('');
    searchValue = '';

    makes = computed(() => {
        return this.storeService.makes();
    });

    selectedModel = signal<string>('');
    models = computed(() => {
        if (!this.selectedMake()) return [];
        else if (this.selectedMake() === 'all') return this.storeService.makes().flatMap((m) => m?.models ?? []);

        const make = this.storeService.makes().find((m) => m.name === this.selectedMake());
        return make?.models ?? [];
    });

    partTypes = computed(() => {
        if (!this.selectedModel()) return [];
        const partTypeIds = this.storeService
            .partTypeModelMaps()
            .filter(
                (m) =>
                    (this.selectedMake() === 'all' || m.make === this.selectedMake()) &&
                    (this.selectedModel() === 'all' || m.model === this.selectedModel())
            )
            .map((m) => m.partTypeId);
        return this.storeService
            .partTypes()
            .filter((p) => partTypeIds.includes(p.id))
            .map((p) => {
                return {
                    id: p.id,
                    name: p.name
                };
            });
    });

    search() {
        this.router.navigate(['/products']);
    }

    searchByPartNumber() {
        const searchValue = (this.searchValue || '').trim();

        this.router.navigate(['/search'], {
            queryParams: { search: searchValue ? searchValue : undefined }
        });
    }

    onMakeChanged(e: any) {
        const make = e.target.value;
        this.selectedMake.set(make);
    }

    onModelChanged(e: any) {
        const model = e.target.value;
        this.selectedModel.set(model);
    }

    onPartTypeChanged(e: any) {
        if (!e.target.value) return;

        const partTypeId = e.target.value !== 'all' ? e.target.value : '';
        const make = this.selectedMake() !== 'all' ? this.selectedMake() : '';
        const model = this.selectedModel() !== 'all' ? this.selectedModel() : '';

        this.router.navigate(['/search'], {
            queryParams: { make: make, model: model, partType: partTypeId }
        });
    }
}
